/**
 * WinbackStep - Discount offer for cancelling users
 *
 * Shows a special offer to retain users who want to cancel.
 */

import type { WinbackPricingResponse } from '@/api/generated-catch-backend/model';
import { WinbackPriceCard } from '@/components/payment-paywall-modal/components/WinbackPriceCard';

interface WinbackStepProps {
  pricing: WinbackPricingResponse | null;
  isPricingLoading: boolean;
  onAccept: () => void;
  onDecline: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function WinbackStep({
  pricing,
  isPricingLoading,
  onAccept,
  onDecline,
  isSubmitting,
  error,
}: WinbackStepProps) {
  // Calculate discount percentage
  const discountPercent = pricing
    ? Math.round((pricing.discountCents / pricing.originalPriceCents) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-catch-dark-blue-90">
          Wait - we have a special offer
        </h2>
        <p className="mt-2 text-catch-slate">
          We&apos;d love for you to stay. How about {discountPercent}% off your membership?
        </p>
      </div>

      {/* Price card */}
      <WinbackPriceCard pricing={pricing} isLoading={isPricingLoading} />

      {/* Error message */}
      {error && (
        <p className="text-center text-sm text-catch-danger">{error}</p>
      )}

      {/* Accept CTA */}
      <button
        onClick={onAccept}
        disabled={isSubmitting || isPricingLoading}
        className="flex w-full items-center justify-center rounded-full bg-catch-avocado-400 px-5 py-4 text-sm font-bold text-catch-dark-blue-90 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? 'Applying...' : 'Claim my discount'}
      </button>

      {/* Decline link */}
      <button
        onClick={onDecline}
        disabled={isSubmitting}
        className="w-full text-center text-sm font-medium text-catch-danger transition-opacity hover:opacity-70 disabled:opacity-50"
      >
        {isSubmitting ? 'Cancelling...' : 'No thanks, cancel anyway'}
      </button>
    </div>
  );
}

export default WinbackStep;
